import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import QRCode from 'qrcode';
import { copyToClipboard } from '../utils/clipboard';

/* ===========================================================================
 * BuyCredits V2 — senior overhaul
 * - Header padronizado igual GenerateCode (saldo + voltar)
 * - Layout 2 colunas com sidebar sticky (resumo)
 * - Presets visuais em grid responsivo
 * - Stepper de quantidade refinado + slider visual
 * - PIX checkout limpo, QR código maior em mobile
 * - Tabela de preços responsiva
 * - Histórico de pedidos com cards
 * - Mobile responsivo (3 breakpoints)
 * - Dark mode dedicado
 *
 * Lógica de negócio inalterada:
 *  - POST /credits/ativopay/create (gateway) ou /credits/purchase (manual)
 *  - Polling em GET /credits/ativopay/status/:orderId a cada 4s
 *  - Geração local de PIX EMV para modo manual
 * ========================================================================= */

const PRESETS = [10, 50, 100, 200, 300, 500, 1000];
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_DURATION_MS = 24 * 60 * 60 * 1000;

/* ---------- helpers PIX EMV (modo manual) ---------- */
const crc16ccitt = (str) => {
  let crc = 0xFFFF;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
    crc &= 0xFFFF;
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
};
const tlv = (id, value) => `${id}${String(value.length).padStart(2, '0')}${value}`;
const buildStaticPixPayload = (chave, amount) => {
  if (!chave || !amount || isNaN(amount)) return '';
  const merchantAccountInfo = tlv('26', tlv('00', 'BR.GOV.BCB.PIX') + tlv('01', chave));
  let payload = '';
  payload += tlv('00', '01');
  payload += tlv('01', '12');
  payload += merchantAccountInfo;
  payload += tlv('52', '0000');
  payload += tlv('53', '986');
  payload += tlv('54', Number(amount).toFixed(2));
  payload += tlv('58', 'BR');
  payload += tlv('59', 'COMEX');
  payload += tlv('60', 'SAO PAULO');
  payload += tlv('62', tlv('05', '***'));
  payload += '6304';
  return payload + crc16ccitt(payload);
};

const fmtBRL = (v) => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;
const fmtCredits = (v) => `${v} crédito${v === 1 ? '' : 's'}`;

/* ===================================================================== */
const BuyCredits = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [pricingTable, setPricingTable] = useState(null);
  const [pricingError, setPricingError] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [paymentSettings, setPaymentSettings] = useState({ mode: 'gateway', pixKey: '', whatsappNumber: '' });

  const [creating, setCreating] = useState(false);
  const [order, setOrder] = useState(null);
  const [paid, setPaid] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('idle');

  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);

  const pollRef = useRef(null);
  const pollStartRef = useRef(0);

  /* ---------- helpers ---------- */
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  }, []);

  /* ---------- carrega pricing + settings ---------- */
  useEffect(() => {
    let cancelled = false;
    api.get('/credits/ativopay/pricing')
      .then((r) => { if (!cancelled) setPricingTable(r.data); })
      .catch(() => { if (!cancelled) setPricingError('Não foi possível carregar a tabela de preços.'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/settings')
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setPaymentSettings({
          mode: d.paymentMode === 'pix_key' ? 'pix_key' : 'gateway',
          pixKey: d.pixKey || '',
          whatsappNumber: d.whatsappNumber || '',
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /* ---------- refresh user ---------- */
  useEffect(() => {
    refreshUser();
    const onFocus = () => refreshUser();
    window.addEventListener('focus', onFocus);
    const interval = setInterval(() => refreshUser(), 30000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- pricing ---------- */
  const getUnitPrice = useCallback((qty) => {
    if (!pricingTable) return 0;
    const tier = pricingTable.tiers.find((t) => qty >= t.min && qty <= t.max);
    return tier ? tier.unitPrice : 0;
  }, [pricingTable]);

  const unitPrice = getUnitPrice(quantity);
  const totalPrice = Number((quantity * unitPrice).toFixed(2));

  /* ---------- QR Code ---------- */
  useEffect(() => {
    if (order && order.pixCopiaCola) {
      QRCode.toDataURL(order.pixCopiaCola, {
        width: 260,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#0f172a', light: '#ffffff' },
      })
        .then(setQrDataUrl)
        .catch((err) => console.error('Erro ao gerar QR:', err));
    } else {
      setQrDataUrl('');
    }
  }, [order]);

  /* ---------- polling ---------- */
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((orderId) => {
    stopPolling();
    pollStartRef.current = Date.now();
    setPollingStatus('polling');

    const tick = async () => {
      if (Date.now() - pollStartRef.current > POLL_MAX_DURATION_MS) {
        stopPolling();
        setPollingStatus('expired');
        return;
      }
      try {
        const r = await api.get(`/credits/ativopay/status/${orderId}`);
        const s = (r.data.status || '').toLowerCase();
        if (s === 'approved') {
          stopPolling();
          setPaid(true);
          setPollingStatus('paid');
          refreshUser();
        } else if (['failed', 'refused', 'canceled', 'cancelled', 'expired', 'chargeback', 'refunded'].includes(s)) {
          stopPolling();
          setPollingStatus('expired');
        }
      } catch (e) { /* tolera erros temporários */ }
    };

    tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
  }, [stopPolling, refreshUser]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  /* ---------- criar pedido ---------- */
  const handleCreate = async (e) => {
    e?.preventDefault();
    setError('');
    setErrors({});

    if (!pricingTable) return;
    if (quantity < pricingTable.minQuantity) {
      setErrors({ quantity: `Quantidade mínima é ${pricingTable.minQuantity}` });
      return;
    }
    if (quantity > pricingTable.maxQuantity) {
      setErrors({ quantity: `Quantidade máxima é ${pricingTable.maxQuantity}` });
      return;
    }

    setCreating(true);
    try {
      if (paymentSettings.mode === 'gateway') {
        const r = await api.post('/credits/ativopay/create', { quantity });
        setOrder({ ...r.data, mode: 'gateway' });
        setPaid(false);
        startPolling(r.data.orderId);
      } else {
        if (!paymentSettings.pixKey) {
          setError('O administrador ainda não cadastrou uma chave PIX. Entre em contato pelo WhatsApp.');
          return;
        }
        const r = await api.post('/credits/purchase', { quantity });
        const pixCopiaCola = buildStaticPixPayload(paymentSettings.pixKey, totalPrice);
        setOrder({
          orderId: r.data.id,
          quantity,
          amount: totalPrice,
          pixCopiaCola,
          mode: 'pix_key',
          status: 'pending',
        });
        setPaid(false);
        setPollingStatus('manual_wait');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.details || 'Erro ao gerar PIX. Tente novamente.';
      setError(msg);
      showToast('error', msg);
    } finally {
      setCreating(false);
    }
  };

  const copyPix = async () => {
    if (!order?.pixCopiaCola) return;
    const ok = await copyToClipboard(order.pixCopiaCola);
    if (ok) {
      setCopied(true);
      showToast('success', 'PIX copiado');
      setTimeout(() => setCopied(false), 2200);
    } else {
      showToast('error', 'Não foi possível copiar');
    }
  };

  const downloadQR = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `comex-pix-pedido-${order?.orderId || 'qr'}.png`;
    a.click();
    showToast('success', 'QR Code baixado');
  };

  const newOrder = () => {
    stopPolling();
    setOrder(null);
    setPaid(false);
    setPollingStatus('idle');
    setQrDataUrl('');
  };

  const credits = user?.credits ?? 0;

  /* ===================================================================== */
  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{styles}</style>

      <div className="bcx">
        {/* ===== HEADER ===== */}
        <header className="bcx-header">
          <div className="bcx-header-inner">
            <div className="bcx-header-left">
              {/* Botão "Voltar" removido — navegação já fica na sidebar (ClientLayout) */}
              <div>
                <h1 className="bcx-header-title">Comprar Créditos</h1>
                <p className="bcx-header-sub">Pagamento via PIX{paymentSettings.mode === 'gateway' ? ' instantâneo' : ' com aprovação manual'}</p>
              </div>
            </div>
            <div className="bcx-header-right">
              <div className={`bcx-balance-chip ${credits === 0 ? 'empty' : credits < 10 ? 'low' : ''}`}>
                <i className="fas fa-coins"></i>
                <div className="bcx-balance-info">
                  <span className="bcx-balance-label">Saldo</span>
                  <span className="bcx-balance-val">{credits}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* ===== TOAST ===== */}
        {toast && (
          <div className={`bcx-toast bcx-toast-${toast.type}`}>
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'}`}></i>
            <span>{toast.msg}</span>
          </div>
        )}

        {/* ===== MAIN ===== */}
        <main className="bcx-main">
          {error && (
            <div className="bcx-alert bcx-alert-error">
              <i className="fas fa-circle-exclamation"></i>
              <span>{error}</span>
              <button onClick={() => setError('')} className="bcx-alert-close" aria-label="Fechar">
                <i className="fas fa-xmark"></i>
              </button>
            </div>
          )}
          {pricingError && (
            <div className="bcx-alert bcx-alert-error">
              <i className="fas fa-circle-exclamation"></i>
              <span>{pricingError}</span>
            </div>
          )}

          {!order && (
            <SelectQuantity
              pricingTable={pricingTable}
              quantity={quantity}
              setQuantity={setQuantity}
              unitPrice={unitPrice}
              totalPrice={totalPrice}
              creating={creating}
              errors={errors}
              onSubmit={handleCreate}
              paymentMode={paymentSettings.mode}
            />
          )}

          {order && !paid && (
            <PixCheckout
              order={order}
              qrDataUrl={qrDataUrl}
              pollingStatus={pollingStatus}
              copied={copied}
              onCopy={copyPix}
              onDownload={downloadQR}
              onCancel={newOrder}
              whatsappNumber={paymentSettings.whatsappNumber}
            />
          )}

          {order && paid && (
            <PaymentSuccess
              order={order}
              user={user}
              onAnother={newOrder}
              onBack={() => navigate('/dashboard')}
            />
          )}

          {!order && <RecentOrders />}
        </main>
      </div>
    </>
  );
};

/* ===================================================================== */
const SelectQuantity = ({ pricingTable, quantity, setQuantity, unitPrice, totalPrice, creating, errors, onSubmit, paymentMode }) => {
  const isManual = paymentMode === 'pix_key';

  if (!pricingTable) {
    return (
      <div className="bcx-card bcx-loading">
        <div className="bcx-spinner"></div>
        <p>Carregando tabela de preços…</p>
      </div>
    );
  }

  const minQ = pricingTable.minQuantity;
  const maxQ = pricingTable.maxQuantity;

  return (
    <form className="bcx-grid" onSubmit={onSubmit}>
      <section className="bcx-card">
        <header className="bcx-card-head">
          <div className="bcx-card-head-icon">
            <i className="fas fa-cubes-stacked"></i>
          </div>
          <div>
            <h2 className="bcx-card-title">Escolha um pacote</h2>
            <p className="bcx-card-sub">Quanto mais créditos, menor o preço por unidade</p>
          </div>
        </header>

        <div className="bcx-presets">
          {PRESETS.map((p) => {
            const tier = pricingTable.tiers.find((t) => p >= t.min && p <= t.max);
            const price = p * (tier?.unitPrice || 0);
            return (
              <button
                key={p}
                type="button"
                className={`bcx-preset ${quantity === p ? 'is-active' : ''}`}
                onClick={() => setQuantity(p)}
              >
                <div className="bcx-preset-qty">{p}</div>
                <div className="bcx-preset-label">créditos</div>
                <div className="bcx-preset-price">{fmtBRL(price)}</div>
                {p >= 200 && <div className="bcx-preset-badge">Economize</div>}
              </button>
            );
          })}
        </div>

        <div className="bcx-custom">
          <label className="bcx-label">Ou digite uma quantidade personalizada</label>
          <div className="bcx-custom-row">
            <button
              type="button"
              className="bcx-stepper"
              onClick={() => setQuantity(Math.max(minQ, quantity - 10))}
              title="−10"
            >
              <i className="fas fa-minus"></i>
            </button>
            <input
              type="number"
              min={minQ}
              max={maxQ}
              value={quantity}
              onChange={(e) => {
                const raw = e.target.value;
                // Deixa digitar livremente (inclusive vazio, inclusive "2" antes de "20")
                // — só protege contra negativo/acima do máximo. O mínimo só é cobrado
                // quando o campo perde o foco ou na hora de confirmar, nunca no meio
                // da digitação (senão trava em 10 e não dá pra digitar 20, 30 etc).
                if (raw === '') { setQuantity(''); return; }
                const v = parseInt(raw, 10);
                if (isNaN(v)) return;
                setQuantity(Math.min(Math.max(v, 0), maxQ));
              }}
              onBlur={() => {
                // Só ao SAIR do campo: se ficou vazio ou abaixo do mínimo, volta pro mínimo.
                if (quantity === '' || Number(quantity) < minQ) setQuantity(minQ);
              }}
              className={`bcx-input ${errors.quantity ? 'is-error' : ''}`}
            />
            <button
              type="button"
              className="bcx-stepper"
              onClick={() => setQuantity(Math.min(maxQ, quantity + 10))}
              title="+10"
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
          <p className="bcx-hint">
            <i className="fas fa-info-circle"></i> Mínimo {minQ} · Máximo {maxQ.toLocaleString('pt-BR')}
          </p>
          {errors.quantity && <p className="bcx-error-text">{errors.quantity}</p>}
        </div>

        <div className="bcx-tiers">
          <h4 className="bcx-tiers-title"><i className="fas fa-tags"></i> Tabela de preços</h4>
          <div className="bcx-tiers-list">
            {pricingTable.tiers.map((t, i) => {
              const inRange = quantity >= t.min && quantity <= t.max;
              return (
                <div key={i} className={`bcx-tier ${inRange ? 'is-current' : ''}`}>
                  <span className="bcx-tier-range">
                    {t.min}{t.max < pricingTable.maxQuantity ? `–${t.max}` : '+'}
                  </span>
                  <span className="bcx-tier-price">
                    {fmtBRL(t.unitPrice)}<small>/un</small>
                  </span>
                  {inRange && <i className="fas fa-check-circle bcx-tier-check"></i>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <aside className="bcx-card bcx-summary">
        <header className="bcx-card-head">
          <div className="bcx-card-head-icon bcx-icon-success">
            <i className="fas fa-receipt"></i>
          </div>
          <div>
            <h2 className="bcx-card-title">Resumo</h2>
            <p className="bcx-card-sub">Confirme antes de pagar</p>
          </div>
        </header>

        <div className="bcx-summary-row">
          <span>Quantidade</span>
          <strong>{fmtCredits(quantity)}</strong>
        </div>
        <div className="bcx-summary-row">
          <span>Preço por crédito</span>
          <strong>{fmtBRL(unitPrice)}</strong>
        </div>
        <div className="bcx-summary-divider" />
        <div className="bcx-summary-total">
          <span>Total a pagar</span>
          <strong className="bcx-summary-total-value">{fmtBRL(totalPrice)}</strong>
        </div>

        <button type="submit" disabled={creating || !pricingTable} className="bcx-cta">
          {creating ? (
            <><span className="bcx-spinner-sm"></span> Gerando PIX…</>
          ) : (
            <><i className="fas fa-qrcode"></i> Gerar PIX e pagar</>
          )}
        </button>

        <ul className="bcx-perks">
          {isManual ? (
            <>
              <li><i className="fas fa-handshake"></i> Atendimento via WhatsApp</li>
              <li><i className="fas fa-shield-halved"></i> PIX direto com chave do admin</li>
              <li><i className="fas fa-user-check"></i> Aprovação manual após comprovante</li>
            </>
          ) : (
            <>
              <li><i className="fas fa-bolt"></i> Liberação automática</li>
              <li><i className="fas fa-shield-halved"></i> Transação segura via gateway</li>
              <li><i className="fas fa-clock-rotate-left"></i> QR válido por 24h</li>
            </>
          )}
        </ul>
      </aside>
    </form>
  );
};

/* ===================================================================== */
const PixCheckout = ({ order, qrDataUrl, pollingStatus, copied, onCopy, onDownload, onCancel, whatsappNumber }) => {
  const expiresAt = order.pixExpiresAt ? new Date(order.pixExpiresAt) : null;
  const expiresFmt = expiresAt
    ? expiresAt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : '24h';
  const isManual = order.mode === 'pix_key';
  const waMessage = encodeURIComponent(
    `Olá! Acabei de fazer um pedido de créditos COMEX.\n\nPedido: #${order.orderId}\nQuantidade: ${order.quantity} créditos\nValor: R$ ${Number(order.amount).toFixed(2).replace('.', ',')}\n\nSegue o comprovante:`
  );
  const waLink = whatsappNumber ? `https://wa.me/${whatsappNumber}?text=${waMessage}` : null;

  return (
    <div className="bcx-grid">
      <section className="bcx-card">
        <header className="bcx-card-head">
          <div className="bcx-card-head-icon bcx-icon-success">
            <i className="fas fa-qrcode"></i>
          </div>
          <div>
            <h2 className="bcx-card-title">Pague com PIX</h2>
            <p className="bcx-card-sub">Escaneie o QR ou copie o código</p>
          </div>
        </header>

        <div className="bcx-qr-wrap">
          {qrDataUrl ? (
            <div className="bcx-qr-frame">
              <img src={qrDataUrl} alt="QR Code PIX" />
              {pollingStatus === 'polling' && (
                <div className="bcx-qr-pulse">
                  <span className="bcx-qr-pulse-dot"></span>
                  Aguardando pagamento…
                </div>
              )}
            </div>
          ) : (
            <div className="bcx-qr-frame bcx-qr-loading">
              <div className="bcx-spinner"></div>
            </div>
          )}
          {qrDataUrl && (
            <button type="button" onClick={onDownload} className="bcx-btn-ghost-sm">
              <i className="fas fa-download"></i> Baixar QR Code
            </button>
          )}
        </div>

        <div className="bcx-pix-copy">
          <label className="bcx-label">PIX Copia e Cola</label>
          <div className="bcx-pix-copy-row">
            <input type="text" value={order.pixCopiaCola || ''} readOnly />
            <button type="button" onClick={onCopy} className={`bcx-btn-primary ${copied ? 'is-copied' : ''}`}>
              {copied ? (<><i className="fas fa-check"></i> Copiado!</>) : (<><i className="fas fa-copy"></i> Copiar</>)}
            </button>
          </div>
        </div>

        <ol className="bcx-steps">
          <li><span className="bcx-step-num">1</span> Abra o app do seu banco</li>
          <li><span className="bcx-step-num">2</span> Vá em PIX → Pix Copia e Cola</li>
          <li><span className="bcx-step-num">3</span> Cole o código e confirme</li>
          {isManual ? (
            <>
              <li><span className="bcx-step-num">4</span> Envie o comprovante pelo WhatsApp</li>
              <li><span className="bcx-step-num">5</span> Aguarde a aprovação do admin</li>
            </>
          ) : (
            <li><span className="bcx-step-num">4</span> Créditos liberados automaticamente</li>
          )}
        </ol>
      </section>

      <aside className="bcx-card bcx-summary">
        <header className="bcx-card-head">
          <div className="bcx-card-head-icon">
            <i className="fas fa-receipt"></i>
          </div>
          <div>
            <h2 className="bcx-card-title">Pedido #{order.orderId}</h2>
            <p className="bcx-card-sub">Aguardando confirmação</p>
          </div>
        </header>

        <div className="bcx-summary-row">
          <span>Créditos</span>
          <strong>{fmtCredits(order.quantity)}</strong>
        </div>
        <div className="bcx-summary-row">
          <span>Valor</span>
          <strong>{fmtBRL(order.amount)}</strong>
        </div>
        {!isManual && (
          <div className="bcx-summary-row">
            <span>Expira em</span>
            <strong>{expiresFmt}</strong>
          </div>
        )}
        <div className="bcx-summary-divider" />

        {pollingStatus === 'polling' && !isManual && (
          <div className="bcx-status bcx-status-waiting">
            <span className="bcx-status-dot"></span>
            <div>
              <strong>Aguardando pagamento…</strong>
              <p>Atualizamos automaticamente assim que detectarmos.</p>
            </div>
          </div>
        )}
        {pollingStatus === 'manual_wait' && isManual && (
          <div className="bcx-status bcx-status-waiting">
            <span className="bcx-status-dot"></span>
            <div>
              <strong>Pagamento manual</strong>
              <p>Envie o comprovante pelo WhatsApp para liberação.</p>
            </div>
          </div>
        )}
        {pollingStatus === 'expired' && (
          <div className="bcx-status bcx-status-expired">
            <i className="fas fa-circle-xmark"></i>
            <div>
              <strong>Pedido não pago / expirado</strong>
              <p>Você pode gerar um novo PIX abaixo.</p>
            </div>
          </div>
        )}

        {isManual && waLink && (
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="bcx-btn-whatsapp bcx-btn-block">
            <i className="fab fa-whatsapp"></i> Enviar comprovante via WhatsApp
          </a>
        )}

        <button onClick={onCancel} className="bcx-btn-ghost bcx-btn-block">
          <i className="fas fa-rotate-left"></i> Cancelar e gerar outro
        </button>
      </aside>
    </div>
  );
};

/* ===================================================================== */
const PaymentSuccess = ({ order, user, onAnother, onBack }) => (
  <div className="bcx-success">
    <div className="bcx-success-icon">
      <svg viewBox="0 0 52 52">
        <circle className="bcx-tick-circle" cx="26" cy="26" r="25" fill="none" />
        <path className="bcx-tick-check" fill="none" d="M14.1 27.2 21.4 34.6 38.1 17.9" />
      </svg>
    </div>
    <h2 className="bcx-success-title">Pagamento confirmado!</h2>
    <p className="bcx-success-sub">
      Seus <strong>{fmtCredits(order.quantity)}</strong> foram adicionados à sua conta.
    </p>

    <div className="bcx-success-grid">
      <div>
        <span>Pedido</span>
        <strong>#{order.orderId}</strong>
      </div>
      <div>
        <span>Valor pago</span>
        <strong>{fmtBRL(order.amount)}</strong>
      </div>
      <div>
        <span>Saldo atual</span>
        <strong>{user?.credits ?? '—'} créditos</strong>
      </div>
    </div>

    <div className="bcx-success-actions">
      <button onClick={onBack} className="bcx-cta">
        <i className="fas fa-house"></i> Ir para o Dashboard
      </button>
      <button onClick={onAnother} className="bcx-btn-ghost">
        <i className="fas fa-plus"></i> Comprar mais
      </button>
    </div>
  </div>
);

/* ===================================================================== */
const RecentOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/credits/orders')
      .then((r) => { if (!cancelled) setOrders(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const statusLabel = (s) => {
    switch ((s || '').toLowerCase()) {
      case 'approved': return 'Aprovado';
      case 'manual': return 'Aprovado (admin)';
      case 'cancelled':
      case 'canceled': return 'Cancelado';
      case 'failed': return 'Falhou';
      case 'expired': return 'Expirado';
      case 'refused': return 'Recusado';
      case 'refunded': return 'Reembolsado';
      default: return 'Aguardando pagamento';
    }
  };

  const handleExpandOrder = async (order) => {
    if (expandedOrder?.id === order.id) {
      setExpandedOrder(null);
      return;
    }

    setExpandedOrder(order);
    
    if (order.pix_qrcode) {
      try {
        const dataUrl = await QRCode.toDataURL(order.pix_qrcode, {
          width: 260,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: '#0f172a', light: '#ffffff' },
        });
        setQrDataUrl(dataUrl);
      } catch (err) {
        console.error('Erro ao gerar QR:', err);
      }
    }
  };

  const copyPix = async (pixCode) => {
    const ok = await copyToClipboard(pixCode);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const downloadQR = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `comex-pix-pedido-${expandedOrder?.id || 'qr'}.png`;
    a.click();
  };

  if (loading) {
    return (
      <section className="bcx-card bcx-orders">
        <h3 className="bcx-orders-title"><i className="fas fa-clock-rotate-left"></i> Pedidos recentes</h3>
        <div className="bcx-loading-line"><div className="bcx-spinner-sm" /> Carregando…</div>
      </section>
    );
  }

  if (!orders.length) {
    return (
      <section className="bcx-card bcx-orders">
        <h3 className="bcx-orders-title"><i className="fas fa-clock-rotate-left"></i> Pedidos recentes</h3>
        <p className="bcx-muted">Nenhum pedido ainda. Faça sua primeira compra acima!</p>
      </section>
    );
  }

  return (
    <section className="bcx-card bcx-orders">
      <h3 className="bcx-orders-title"><i className="fas fa-clock-rotate-left"></i> Pedidos recentes</h3>
      <div className="bcx-orders-list">
        {orders.slice(0, 8).map((o) => (
          <div key={o.id}>
            <div 
              className={`bcx-order bcx-order-${(o.status || 'pending').toLowerCase()} ${expandedOrder?.id === o.id ? 'is-expanded' : ''}`}
              style={{ cursor: (o.pix_qrcode || o.status === 'pending') ? 'pointer' : 'default' }}
              onClick={() => (o.pix_qrcode || o.status === 'pending') && handleExpandOrder(o)}
            >
              <div className="bcx-order-left">
                <div className="bcx-order-id">#{o.id}</div>
                <div className="bcx-order-date">
                  {new Date(o.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                </div>
              </div>
              <div className="bcx-order-mid">
                <strong>{o.quantity} créditos</strong>
                {o.amount_cents ? <span>{fmtBRL(o.amount_cents / 100)}</span> : null}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className={`bcx-badge bcx-badge-${(o.status || 'pending').toLowerCase()}`}>
                  {statusLabel(o.status)}
                </span>
                {(o.pix_qrcode || o.status === 'pending') && (
                  <i className={`fas fa-chevron-${expandedOrder?.id === o.id ? 'up' : 'down'}`} style={{ color: '#f90', fontSize: '12px' }}></i>
                )}
              </div>
            </div>

            {expandedOrder?.id === o.id && (o.pix_qrcode || o.status === 'pending') && (
              <div className="bcx-order-expanded">
                <div className="bcx-order-expanded-content">
                  {qrDataUrl && (
                    <div className="bcx-qr-section">
                      <h4>QR Code PIX</h4>
                      <img src={qrDataUrl} alt="QR Code PIX" className="bcx-qr-image" />
                      <button onClick={downloadQR} className="bcx-btn-download">
                        <i className="fas fa-download"></i> Baixar QR Code
                      </button>
                    </div>
                  )}

                  {o.pix_qrcode && (
                    <div className="bcx-pix-section">
                      <h4>Cópia e Cola</h4>
                      <div className="bcx-pix-code">
                        <code>{o.pix_qrcode}</code>
                        <button 
                          onClick={() => copyPix(o.pix_qrcode)}
                          className={`bcx-btn-copy ${copied ? 'is-copied' : ''}`}
                        >
                          <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                          {copied ? 'Copiado!' : 'Copiar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ===================================================================== */
const styles = `
  .bcx, .bcx *, .bcx *::before, .bcx *::after { margin: 0; padding: 0; box-sizing: border-box; }
  .bcx {
    font-family: 'Lexend', sans-serif;
    background: linear-gradient(180deg, #f5f5f7 0%, #ececef 100%);
    min-height: 100vh;
    color: #333;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }

  /* ===== HEADER ===== */
  .bcx-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 100;
  }
  .bcx-header-inner {
    max-width: 1280px; margin: 0 auto; padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
  }
  .bcx-header-left { display: flex; align-items: center; gap: 14px; }
  .bcx-back-btn {
    width: 40px; height: 40px; border-radius: 10px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.10);
    color: #f90; cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center; font-size: 16px;
  }
  .bcx-back-btn:hover { background: rgba(255,153,0,0.15); transform: translateX(-2px); }
  .bcx-header-title { font-size: 19px; font-weight: 700; color: #fff; line-height: 1.1; }
  .bcx-header-sub { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 2px; }
  .bcx-header-right { display: flex; align-items: center; gap: 12px; }
  .bcx-balance-chip {
    display: flex; align-items: center; gap: 12px;
    background: rgba(255,153,0,0.13); border: 1px solid rgba(255,153,0,0.30);
    padding: 8px 16px; border-radius: 12px; color: #fff; transition: all .3s;
  }
  .bcx-balance-chip i { color: #f90; font-size: 18px; }
  .bcx-balance-chip.low { background: rgba(245,158,11,0.18); border-color: rgba(245,158,11,0.50); }
  .bcx-balance-chip.empty { background: rgba(229,62,62,0.18); border-color: rgba(229,62,62,0.50); }
  .bcx-balance-info { display: flex; flex-direction: column; line-height: 1.1; }
  .bcx-balance-label { font-size: 10px; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: .5px; }
  .bcx-balance-val { font-size: 17px; font-weight: 700; }

  /* ===== TOAST ===== */
  .bcx-toast {
    position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
    background: #1a1a2e; color: #fff; padding: 12px 22px; border-radius: 12px;
    display: flex; align-items: center; gap: 10px; z-index: 9999;
    box-shadow: 0 12px 40px rgba(0,0,0,0.25); font-weight: 500; font-size: 14px;
    animation: bcxToastIn .25s ease;
  }
  .bcx-toast i { font-size: 18px; }
  .bcx-toast-success i { color: #22c55e; }
  .bcx-toast-error i { color: #ef4444; }
  .bcx-toast-warn i { color: #f59e0b; }
  @keyframes bcxToastIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }

  /* ===== MAIN ===== */
  .bcx-main { max-width: 1280px; margin: 0 auto; padding: 28px 24px 60px; }

  /* ===== ALERTS ===== */
  .bcx-alert {
    display: flex; align-items: center; gap: 12px;
    border-radius: 12px; padding: 14px 16px; font-size: 14px; margin-bottom: 18px;
  }
  .bcx-alert-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
  .bcx-alert-close {
    margin-left: auto; background: transparent; border: 0; color: inherit;
    cursor: pointer; opacity: .7; padding: 4px;
  }
  .bcx-alert-close:hover { opacity: 1; }

  /* ===== GRID ===== */
  .bcx-grid { display: grid; grid-template-columns: 1.5fr 1fr; gap: 22px; align-items: start; }

  /* ===== CARD ===== */
  .bcx-card {
    background: #fff; border: 1px solid rgba(0,0,0,0.04);
    border-radius: 20px; padding: 26px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  }
  .bcx-card-head {
    display: flex; align-items: center; gap: 14px; margin-bottom: 22px;
  }
  .bcx-card-head-icon {
    width: 50px; height: 50px; border-radius: 14px;
    background: linear-gradient(135deg, #f90, #ff6600);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    font-size: 20px; box-shadow: 0 4px 15px rgba(255,153,0,0.30);
  }
  .bcx-card-head-icon.bcx-icon-success {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    box-shadow: 0 4px 15px rgba(34,197,94,0.30);
  }
  .bcx-card-title { font-size: 19px; font-weight: 700; line-height: 1.2; margin: 0; color: #222; }
  .bcx-card-sub { font-size: 13px; color: #888; margin: 4px 0 0; }

  .bcx-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; gap: 16px; color: #64748b;
  }
  .bcx-loading-line { display: flex; align-items: center; gap: 10px; color: #64748b; font-size: 14px; }

  /* ===== PRESETS ===== */
  .bcx-presets {
    display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; margin-bottom: 20px;
  }
  .bcx-preset {
    position: relative; background: #fafafa; border: 2px solid #ececec; border-radius: 14px;
    padding: 14px 10px; cursor: pointer; transition: all .25s;
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    font-family: 'Lexend', sans-serif;
  }
  .bcx-preset:hover { border-color: #f90; background: #fff5e6; transform: translateY(-2px); }
  .bcx-preset.is-active {
    background: linear-gradient(135deg, #fff5e6, #ffedd5);
    border-color: #f90;
    box-shadow: 0 8px 20px rgba(255,153,0,0.20);
  }
  .bcx-preset-qty { font-size: 22px; font-weight: 800; color: #222; }
  .bcx-preset-label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: .5px; }
  .bcx-preset-price { font-size: 12px; color: #f90; font-weight: 700; margin-top: 4px; }
  .bcx-preset-badge {
    position: absolute; top: -8px; right: -4px;
    background: linear-gradient(135deg, #22c55e, #16a34a); color: #fff;
    padding: 2px 8px; border-radius: 8px; font-size: 9px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .5px;
    box-shadow: 0 4px 10px rgba(34,197,94,0.30);
  }

  /* ===== CUSTOM ===== */
  .bcx-custom {
    padding: 18px; background: #f8f9fa; border: 1px solid #efefef; border-radius: 14px; margin-bottom: 20px;
  }
  .bcx-label { display: block; font-size: 12px; font-weight: 700; color: #666; margin-bottom: 10px; text-transform: uppercase; letter-spacing: .4px; }
  .bcx-custom-row { display: flex; gap: 10px; align-items: center; }
  .bcx-stepper {
    width: 46px; height: 46px;
    background: #fff; border: 2px solid #e0e0e0; border-radius: 10px;
    font-size: 18px; color: #666;
    cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Lexend', sans-serif;
  }
  .bcx-stepper:hover { background: #fff5e6; border-color: #f90; color: #f90; }
  .bcx-input {
    flex: 1; height: 46px;
    border: 2px solid #e0e0e0; border-radius: 10px;
    padding: 0 14px; text-align: center;
    font-size: 20px; font-weight: 700; font-family: inherit; color: #222;
    outline: none; transition: all .2s; background: #fff;
  }
  .bcx-input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,.15); }
  .bcx-input.is-error { border-color: #e53e3e; box-shadow: 0 0 0 3px rgba(229,62,62,.12); }
  .bcx-hint { font-size: 12px; color: #999; margin-top: 8px; text-align: center; display: flex; align-items: center; justify-content: center; gap: 4px; }
  .bcx-hint i { font-size: 10px; }
  .bcx-error-text { font-size: 13px; color: #e53e3e; margin-top: 8px; text-align: center; }

  /* ===== TIERS ===== */
  .bcx-tiers { padding: 18px; background: #f8f9fa; border: 1px solid #efefef; border-radius: 14px; }
  .bcx-tiers-title { font-size: 14px; font-weight: 700; color: #222; margin: 0 0 14px; display: inline-flex; align-items: center; gap: 8px; }
  .bcx-tiers-title i { color: #f90; }
  .bcx-tiers-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .bcx-tier {
    position: relative; display: flex; justify-content: space-between; align-items: center;
    background: #fff; border: 1.5px solid #efefef; border-radius: 10px;
    padding: 11px 14px; font-size: 13px; transition: all .2s;
  }
  .bcx-tier.is-current {
    background: linear-gradient(135deg, #fff5e6, #ffedd5);
    border-color: #f90;
    box-shadow: 0 4px 12px rgba(255,153,0,0.15);
  }
  .bcx-tier-range { color: #666; font-weight: 600; }
  .bcx-tier-price { color: #f90; font-weight: 700; }
  .bcx-tier-price small { font-weight: 500; color: #999; }
  .bcx-tier-check { position: absolute; top: -8px; right: -6px; color: #22c55e; font-size: 16px; background: #fff; border-radius: 50%; }

  /* ===== SUMMARY ===== */
  .bcx-summary { position: sticky; top: 90px; }
  .bcx-summary-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 0; font-size: 13px; color: #666;
  }
  .bcx-summary-row strong { color: #222; font-weight: 700; font-size: 14px; }
  .bcx-summary-divider { height: 1px; background: #efefef; margin: 6px 0; }
  .bcx-summary-total {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 14px 0 22px; font-size: 13px; color: #666;
  }
  .bcx-summary-total-value {
    font-size: 30px; font-weight: 800;
    background: linear-gradient(135deg, #f90, #ff6600);
    -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  /* ===== CTA ===== */
  .bcx-cta {
    width: 100%;
    background: linear-gradient(135deg, #f90, #ff6600);
    color: #fff; border: 0; border-radius: 12px;
    padding: 15px 18px; font-size: 15px; font-weight: 700;
    cursor: pointer; transition: all .3s;
    display: inline-flex; align-items: center; justify-content: center; gap: 10px;
    box-shadow: 0 6px 20px rgba(255,153,0,.30);
    font-family: inherit;
  }
  .bcx-cta:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(255,153,0,.40); }
  .bcx-cta:disabled { opacity: .55; cursor: not-allowed; transform: none; box-shadow: none; }

  .bcx-perks {
    margin: 18px 0 0; padding: 0; list-style: none;
    display: grid; gap: 10px;
  }
  .bcx-perks li {
    font-size: 13px; color: #666;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .bcx-perks i { color: #22c55e; width: 16px; text-align: center; }

  /* ===== QR ===== */
  .bcx-qr-wrap { display: flex; flex-direction: column; align-items: center; gap: 18px; margin-bottom: 24px; }
  .bcx-qr-frame {
    background: #fff; padding: 14px; border-radius: 16px;
    border: 2px solid #c6f6d5; position: relative;
    min-width: 240px; min-height: 240px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 10px 30px rgba(34,197,94,0.12);
  }
  .bcx-qr-frame img { display: block; width: 220px; height: 220px; }
  .bcx-qr-loading { background: #f8f9fa; border-color: #efefef; box-shadow: none; }
  .bcx-qr-pulse {
    position: absolute; bottom: -16px; left: 50%; transform: translateX(-50%);
    background: #fff; border: 1px solid #fed7aa; color: #c2410c;
    border-radius: 999px; padding: 6px 14px;
    font-size: 12px; font-weight: 600; white-space: nowrap;
    display: inline-flex; align-items: center; gap: 8px;
    box-shadow: 0 4px 12px rgba(255,153,0,.15);
  }
  .bcx-qr-pulse-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #f90; animation: bcx-pulse 1.4s infinite;
  }
  @keyframes bcx-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: .4; transform: scale(1.3); }
  }
  .bcx-btn-ghost-sm {
    background: #fff; color: #64748b; border: 1px solid #e0e0e0;
    padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: 'Lexend', sans-serif; transition: all .2s;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .bcx-btn-ghost-sm:hover { color: #f90; border-color: #f90; background: #fff5e6; }

  .bcx-pix-copy { background: #f8f9fa; padding: 16px; border-radius: 14px; border: 1px solid #efefef; margin-bottom: 22px; }
  .bcx-pix-copy-row { display: flex; gap: 10px; }
  .bcx-pix-copy input {
    flex: 1; min-width: 0; padding: 12px 16px;
    font-family: 'Courier New', monospace; font-size: 12px;
    background: #fff; border: 1px solid #e0e0e0; border-radius: 10px;
    color: #666; overflow: hidden; text-overflow: ellipsis;
  }

  /* ===== BUTTONS ===== */
  .bcx-btn-primary {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: #fff; border: 0; border-radius: 10px;
    padding: 12px 18px; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: all .25s;
    display: inline-flex; align-items: center; gap: 8px; white-space: nowrap;
    font-family: inherit;
    box-shadow: 0 4px 14px rgba(34,197,94,.25);
  }
  .bcx-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(34,197,94,.40); }
  .bcx-btn-primary.is-copied { background: linear-gradient(135deg, #22c55e, #15803d); }

  .bcx-btn-ghost {
    background: #fff; color: #f90; border: 2px solid #f90;
    border-radius: 10px; padding: 11px 18px; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: all .25s;
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    font-family: inherit;
  }
  .bcx-btn-ghost:hover { background: #fff5e6; transform: translateY(-1px); }
  .bcx-btn-block { width: 100%; margin-top: 12px; }

  .bcx-btn-whatsapp {
    background: linear-gradient(135deg, #25D366, #128C7E);
    color: #fff; border: 0; border-radius: 10px;
    padding: 12px 18px; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: all .25s;
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    font-family: inherit; text-decoration: none;
    box-shadow: 0 4px 14px rgba(37,211,102,.30);
  }
  .bcx-btn-whatsapp:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,211,102,.45); }
  .bcx-btn-whatsapp i { font-size: 18px; }

  /* ===== STEPS ===== */
  .bcx-steps { list-style: none; padding: 0; margin: 0; display: grid; gap: 12px; }
  .bcx-steps li { display: flex; align-items: center; gap: 12px; font-size: 14px; color: #555; font-weight: 500; }
  .bcx-step-num {
    width: 26px; height: 26px;
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
    border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0;
    box-shadow: 0 3px 8px rgba(255,153,0,.30);
  }

  /* ===== STATUS ===== */
  .bcx-status {
    display: flex; gap: 12px; padding: 16px;
    border-radius: 12px; margin-bottom: 14px;
  }
  .bcx-status-waiting { background: linear-gradient(135deg, #fff5e6, #ffedd5); border: 1px solid #fed7aa; color: #9a3412; }
  .bcx-status-expired { background: rgba(229,62,62,0.08); border: 1px solid rgba(229,62,62,0.20); color: #991b1b; }
  .bcx-status strong { display: block; font-size: 14px; margin-bottom: 4px; color: inherit; }
  .bcx-status p { font-size: 12px; color: inherit; opacity: .85; margin: 0; }
  .bcx-status-dot {
    width: 12px; height: 12px; border-radius: 50%;
    background: #f90; flex-shrink: 0; margin-top: 4px;
    box-shadow: 0 0 0 0 rgba(255,153,0,.6);
    animation: bcx-pulse-ring 1.6s infinite;
  }
  @keyframes bcx-pulse-ring {
    0% { box-shadow: 0 0 0 0 rgba(255,153,0,.6); }
    70% { box-shadow: 0 0 0 10px rgba(255,153,0,0); }
    100% { box-shadow: 0 0 0 0 rgba(255,153,0,0); }
  }
  .bcx-status-expired i { font-size: 22px; flex-shrink: 0; margin-top: 2px; color: #ef4444; }

  /* ===== SUCCESS ===== */
  .bcx-success {
    background: linear-gradient(135deg, #1a1a2e, #16213e);
    border: 1px solid rgba(255,153,0,0.20); border-radius: 20px;
    padding: 50px 30px; text-align: center;
    box-shadow: 0 10px 40px rgba(0,0,0,0.20);
    position: relative; overflow: hidden;
  }
  .bcx-success::before {
    content: ''; position: absolute; top: -50%; right: -20%;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255,153,0,0.15), transparent 70%);
  }
  .bcx-success > * { position: relative; z-index: 1; }
  .bcx-success-icon { width: 90px; height: 90px; margin: 0 auto 24px; }
  .bcx-success-icon svg { width: 100%; height: 100%; }
  .bcx-tick-circle {
    stroke: #f90; stroke-width: 2; stroke-miterlimit: 10;
    stroke-dasharray: 166; stroke-dashoffset: 166;
    animation: bcx-stroke .8s cubic-bezier(0.65,0,.45,1) forwards;
  }
  .bcx-tick-check {
    stroke: #f90; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round;
    stroke-dasharray: 48; stroke-dashoffset: 48;
    animation: bcx-stroke .4s cubic-bezier(0.65,0,.45,1) .8s forwards;
  }
  @keyframes bcx-stroke { to { stroke-dashoffset: 0; } }
  .bcx-success-title { font-size: 28px; font-weight: 700; margin: 0 0 10px; color: #fff; }
  .bcx-success-sub { font-size: 16px; color: rgba(255,255,255,0.75); margin: 0 0 28px; }
  .bcx-success-sub strong { color: #f90; }
  .bcx-success-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px;
    background: rgba(255,255,255,0.08); padding: 22px; border-radius: 14px;
    max-width: 540px; margin: 0 auto 28px;
  }
  .bcx-success-grid > div { display: flex; flex-direction: column; gap: 4px; }
  .bcx-success-grid span { font-size: 11px; color: rgba(255,255,255,0.7); text-transform: uppercase; letter-spacing: .4px; }
  .bcx-success-grid strong { font-size: 17px; color: #fff; font-weight: 700; }
  .bcx-success-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

  /* ===== ORDER EXPANDED ===== */
  .bcx-order-expanded {
    background: #f8f9fa; border-top: 1px solid #efefef; padding: 20px;
    animation: bcx-expand-in .3s ease;
  }
  @keyframes bcx-expand-in { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 500px; } }
  .bcx-order-expanded-content {
    display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  }
  @media (max-width: 640px) { .bcx-order-expanded-content { grid-template-columns: 1fr; } }

  .bcx-qr-section, .bcx-pix-section {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
  }
  .bcx-qr-section h4, .bcx-pix-section h4 {
    font-size: 13px; font-weight: 700; color: #000; text-transform: uppercase; letter-spacing: .4px;
    margin: 0;
  }
  .bcx-qr-image {
    width: 200px; height: 200px; border: 2px solid #efefef; border-radius: 12px; padding: 8px; background: #fff;
  }
  .bcx-btn-download {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
    border: 0; border-radius: 10px; padding: 10px 16px; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; gap: 8px;
  }
  .bcx-btn-download:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(255,153,0,.3); }

  .bcx-pix-code {
    display: flex; flex-direction: column; gap: 10px; width: 100%;
  }
  .bcx-pix-code code {
    background: #fff; border: 1px solid #efefef; border-radius: 10px; padding: 12px;
    font-size: 11px; color: #666; word-break: break-all; font-family: monospace;
    max-height: 100px; overflow-y: auto;
  }
  .bcx-btn-copy {
    background: #fff; border: 2px solid #f90; color: #f90; border-radius: 10px;
    padding: 10px 16px; font-size: 13px; font-weight: 700; cursor: pointer;
    transition: all .2s; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  }
  .bcx-btn-copy:hover { background: #fff5e6; }
  .bcx-btn-copy.is-copied { background: #f90; color: #fff; border-color: #f90; }

  /* ===== SPINNER ===== */
  .bcx-spinner {
    width: 40px; height: 40px; border-radius: 50%;
    border: 3px solid #f0f0f0; border-top-color: #f90;
    animation: bcx-spin .8s linear infinite;
  }
  .bcx-spinner-sm {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
    animation: bcx-spin .8s linear infinite;
  }
  @keyframes bcx-spin { to { transform: rotate(360deg); } }

  /* ===== ORDERS ===== */
  .bcx-orders { margin-top: 24px; }
  .bcx-orders-title {
    font-size: 17px; font-weight: 700; color: #222;
    margin: 0 0 16px; display: inline-flex; align-items: center; gap: 10px;
  }
  .bcx-orders-title i { color: #f90; }
  .bcx-orders-list { display: grid; gap: 10px; }
  .bcx-order {
    display: grid; grid-template-columns: auto 1fr auto; gap: 16px; align-items: center;
    background: #f8f9fa; border: 1px solid #efefef; border-radius: 12px;
    padding: 14px 18px; transition: all .2s;
  }
  .bcx-order:hover { background: #fff; border-color: #e0e0e0; box-shadow: 0 4px 14px rgba(0,0,0,0.06); transform: translateY(-1px); }
  .bcx-order-left { display: flex; flex-direction: column; }
  .bcx-order-id { font-weight: 700; color: #222; font-size: 14px; }
  .bcx-order-date { font-size: 12px; color: #999; }
  .bcx-order-mid { display: flex; flex-direction: column; align-items: flex-start; }
  .bcx-order-mid strong { font-size: 14px; color: #222; }
  .bcx-order-mid span { font-size: 12px; color: #666; }
  .bcx-badge {
    padding: 5px 12px; border-radius: 20px; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: .3px; white-space: nowrap;
  }
  .bcx-badge-approved { background: #f0fdf4; color: #16a34a; }
  .bcx-badge-manual { background: #faf5ff; color: #a855f7; }
  .bcx-badge-pending { background: #fffaf0; color: #ea580c; }
  .bcx-badge-cancelled,
  .bcx-badge-canceled,
  .bcx-badge-failed,
  .bcx-badge-expired,
  .bcx-badge-refused { background: #fef2f2; color: #dc2626; }
  .bcx-badge-refunded { background: #eff6ff; color: #2563eb; }

  .bcx-muted { color: #999; font-size: 14px; margin: 0; }

  /* ============== RESPONSIVE ============== */
  /* Monitor estreito / vertical (~1080px): empilha simulador e resumo */
  @media (max-width: 1280px) {
    .bcx-grid { grid-template-columns: 1fr; }
    .bcx-summary { position: static; }
  }
  @media (max-width: 720px) {
    .bcx-header-inner { padding: 12px 16px; flex-wrap: wrap; gap: 10px; }
    .bcx-header-title { font-size: 17px; }
    .bcx-header-sub { display: none; }
    .bcx-header-right { width: 100%; justify-content: flex-end; }
    .bcx-main { padding: 22px 14px 50px; }
    .bcx-card { padding: 22px 18px; border-radius: 16px; }
    .bcx-card-title { font-size: 17px; }
    .bcx-card-head-icon { width: 44px; height: 44px; font-size: 17px; }
    .bcx-presets { grid-template-columns: repeat(2, minmax(0,1fr)); }
    .bcx-tiers-list { grid-template-columns: 1fr; }
    .bcx-pix-copy-row { flex-direction: column; }
    .bcx-pix-copy-row .bcx-btn-primary { width: 100%; justify-content: center; }
    .bcx-success { padding: 36px 20px; }
    .bcx-success-title { font-size: 24px; }
    .bcx-success-grid { grid-template-columns: 1fr; }
    .bcx-success-actions { flex-direction: column-reverse; }
    .bcx-success-actions button { width: 100%; justify-content: center; }
    .bcx-order { grid-template-columns: 1fr; gap: 8px; padding: 12px 14px; }
    .bcx-order-mid { flex-direction: row; gap: 8px; align-items: baseline; }
    .bcx-badge { align-self: flex-start; }
  }
  @media (max-width: 480px) {
    .bcx-balance-chip { padding: 6px 12px; gap: 8px; }
    .bcx-balance-val { font-size: 15px; }
    .bcx-summary-total-value { font-size: 26px; }
    .bcx-qr-frame { min-width: 220px; min-height: 220px; }
    .bcx-qr-frame img { width: 200px; height: 200px; }
  }
`;

export default BuyCredits;
